import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Button,
  Select,
  Empty,
  Input,
  Tabs,
  Form,
  Tooltip,
  Switch,
  Modal,
} from "antd";
import CBInputNumber from "../../components/CBInputNumber";
import {
  CheckCircleOutlined,
  ReloadOutlined,
  EditOutlined,
  LockOutlined,
  MailOutlined,
  RightOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import Layout from "../../components/Layout";
import PageHeader from "../../components/PageHeader";
import axiosInstance from "../../variables/api/axiosInstance";
import { formatDate, formatDateHeure, can, HAB } from "../../variables/constants";
import {
  destinatairesRelance, destinatairesDossier, compteRelances,
} from './destinatairesRelance';
import { toast } from "react-toastify";
import CBModal from "../../components/CBModal";
import KpiCards from "../../components/KpiCards";
import { useTranslation } from "react-i18next";
import { avecLibelleTraduit, libellesTraduits } from "../../i18n/libelles";
import { localeIntl } from "../../i18n/format";
import { libelleDelai, libelleTraduit } from "../../i18n/donnees";
const { Option } = Select;

const niveauConfig = avecLibelleTraduit({
  CRITIQUE: { color: "#ef4444", bg: "#fee2e2" },
  URGENT: { color: "#f97316", bg: "#ffedd5" },
  ATTENTION: { color: "#d5c93b", bg: "#fef9c3" },
  NORMAL: { color: "#1a6b3c", bg: "#e8f5ed" },
}, "delais:niveaux");

const CATEGORIE_LABELS = libellesTraduits("delais:categories");

const TYPE_LABELS = libellesTraduits("delais:types");

const UNITE_LABELS = libellesTraduits("delais:unites");

//  Helpers 
const NIVEAU_ORDER = { CRITIQUE: 4, URGENT: 3, ATTENTION: 2, NORMAL: 1 };
const tagColor = (v) =>
  v === "CRITIQUE"
    ? "red"
    : v === "URGENT"
      ? "orange"
      : v === "ATTENTION"
        ? "gold"
        : "green";

function groupBySinistre(alertes) {
  const map = {};
  for (const a of alertes) {
    const key = a.sinistreTrackingId || a.sinistreNumeroLocal || String(a.id);
    if (!map[key]) {
      map[key] = {
        key,
        sinistreNumeroLocal: a.sinistreNumeroLocal,
        sinistreTrackingId: a.sinistreTrackingId,
        delais: [],
        pireNiveau: "NORMAL",
        prochainEcheance: null,
      };
    }
    map[key].delais.push(a);
    if (NIVEAU_ORDER[a.niveauAlerte] > NIVEAU_ORDER[map[key].pireNiveau]) {
      map[key].pireNiveau = a.niveauAlerte;
    }
    if (
      !map[key].prochainEcheance ||
      a.joursRestants < map[key].prochainEcheance.joursRestants
    ) {
      map[key].prochainEcheance = a;
    }
  }
  // Tri : critiques en premier, puis urgents, puis par échéance
  return Object.values(map).sort((a, b) => {
    const diff = NIVEAU_ORDER[b.pireNiveau] - NIVEAU_ORDER[a.pireNiveau];
    if (diff !== 0) return diff;
    return (
      (a.prochainEcheance?.joursRestants ?? 999) -
      (b.prochainEcheance?.joursRestants ?? 999)
    );
  });
}

/* Jour et mois seuls : la pastille doit tenir dans une colonne de table. La date
   complète, avec l'heure, reste dans l'infobulle. */
const dateCourte = (v) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString(localeIntl(), { day: "2-digit", month: "2-digit" });
};

/**
 * Ce qui a déjà été envoyé sur un délai - ou sur l'ensemble des délais d'un
 * dossier quand on lui en passe plusieurs.
 *
 * Sans ce repère, rien ne distinguait à l'écran un dossier jamais relancé d'un
 * dossier relancé trois fois le matin même : l'agent renvoyait un mail au
 * destinataire pour le savoir.
 */
function BadgeRelances({ delais }) {
  const { t } = useTranslation("delais");
  const lignes = Array.isArray(delais) ? delais : [delais].filter(Boolean);
  const total = lignes.reduce((n, d) => n + (d?.nombreAlertes || 0), 0);
  if (total === 0) {
    return <span style={{ fontSize: 11.5, color: "#9ca3af" }}>{t("relances.jamais")}</span>;
  }
  const derniere = lignes
    .map((d) => d?.derniereAlerteEnvoyee)
    .filter(Boolean)
    .sort()
    .pop();
  return (
    <Tooltip
      title={
        derniere
          ? t("relances.dernierEnvoi", { date: formatDateHeure(derniere) })
          : t("relances.dateInconnue")
      }
    >
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 11.5,
          fontWeight: 600,
          color: "#9a3412",
          background: "#ffedd5",
          border: "1px solid #fed7aa",
          borderRadius: 20,
          padding: "2px 10px",
          whiteSpace: "nowrap",
        }}
      >
        <MailOutlined style={{ fontSize: 11 }} />
        {t("relances.nbEnvois", { count: total })}
        {dateCourte(derniere) && (
          <span style={{ fontWeight: 500, opacity: 0.85 }}>· {dateCourte(derniere)}</span>
        )}
      </span>
    </Tooltip>
  );
}

//  Sous-table des délais d'un sinistre
/* ── Les délais d'un dossier, en branches ───────────────────────────────────

   REPRIS DE L'ÉCRAN « SINISTRES LIÉS », et non un tableau imbriqué.

   Un `<Table>` dans une ligne dépliée apporte son propre en-tête, ses propres
   bordures et sa propre grille : deux grilles s'empilent, décalées l'une par
   rapport à l'autre, et l'oeil ne sait plus laquelle lire. Le décalage se
   corrigeait par une marge gauche posée à la main - une valeur en dur qui ne
   correspondait à rien et se désalignait au premier changement de colonne.

   Les branches disent la même chose autrement : un filet vertical descend du
   dossier, un filet horizontal rejoint chaque délai, et le dernier coupe la
   verticale à mi-hauteur pour que le trait ne continue pas dans le vide. La
   gouttière est une constante partagée, donc l'alignement ne peut plus dériver. */

const GOUTTIERE = 26;

function EnteteDelais() {
  const { t } = useTranslation('delais');
  const style = {
    fontSize: 10.5, fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: 0.3,
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
      <div style={{ width: GOUTTIERE, flexShrink: 0 }} />
      <div style={{
        flex: 1, minWidth: 0, display: 'flex', alignItems: 'center',
        gap: 10, padding: '6px 10px',
      }}>
        <span style={{ ...style, width: 96, flexShrink: 0 }}>{t('dossier.colonnes.niveau')}</span>
        <span style={{ ...style, flex: 1, minWidth: 0 }}>{t('dossier.colonnes.delai')}</span>
        <span style={{ ...style, width: 120, flexShrink: 0 }}>{t('dossier.colonnes.echeance')}</span>
        <span style={{ ...style, width: 72, flexShrink: 0, textAlign: 'center' }}>{t('dossier.colonnes.restant')}</span>
        <span style={{ ...style, width: 120, flexShrink: 0, textAlign: 'center' }}>{t('dossier.colonnes.relances')}</span>
        {/* Réserve la place des deux boutons, pour que les colonnes ne se
            décalent pas d'une ligne à l'autre. */}
        <span style={{ width: 104, flexShrink: 0 }} />
      </div>
    </div>
  );
}

/** Un délai du dossier. Les connecteurs sont deux bordures, comme dans « Sinistres liés ». */
function BrancheDelai({ delai, derniere, onResoudre, onRelancer, relanceEnCours }) {
  const { t } = useTranslation('delais');
  const niveau = niveauConfig[delai.niveauAlerte] || { label: delai.niveauAlerte };
  const jours = delai.joursRestants;
  const couleurJours = jours <= 0 ? '#ef4444' : jours <= 7 ? '#f97316' : '#1a6b3c';
  const expire = delai.dateEcheance && new Date(delai.dateEcheance) < new Date();
  const enCours = relanceEnCours?.(delai.id);

  return (
    <div style={{ display: 'flex', alignItems: 'stretch' }}>
      <div style={{ width: GOUTTIERE, position: 'relative', flexShrink: 0 }}>
        <span style={{
          position: 'absolute', left: 9, top: 0,
          bottom: derniere ? '50%' : 0,
          borderLeft: '1px solid #d9e2dc',
        }}
        />
        <span style={{
          position: 'absolute', left: 9, top: '50%', width: 12,
          borderTop: '1px solid #d9e2dc',
        }}
        />
      </div>

      <div style={{
        flex: 1, minWidth: 0, display: 'flex', alignItems: 'center',
        gap: 10, padding: '7px 10px', borderRadius: 8,
      }}>
        <span style={{ width: 96, flexShrink: 0 }}>
          <Tag color={tagColor(delai.niveauAlerte)} style={{ borderRadius: 20, fontSize: 11, margin: 0 }}>
            {niveau.label}
          </Tag>
        </span>

        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            fontSize: 13, color: '#111827', display: 'block',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {libelleDelai(delai)}
          </span>
          {/* LA REFERENCE JURIDIQUE SOUS LE LIBELLE, et non dans sa propre colonne :
              elle est vide pour les delais internes du bureau, et une colonne a moitie
              vide se lit comme une donnee manquante. */}
          {delai.referenceJuridique && (
            <span style={{ fontSize: 11, color: '#6b7280' }}>{delai.referenceJuridique}</span>
          )}
        </span>

        <span style={{
          width: 120, flexShrink: 0, fontWeight: 600, fontSize: 12,
          color: expire ? '#ef4444' : '#374151',
        }}>
          {formatDate(delai.dateEcheance)}
        </span>

        <span style={{
          width: 72, flexShrink: 0, textAlign: 'center',
          color: couleurJours, fontWeight: 700, fontSize: 13,
        }}>
          {jours <= 0 ? t('echeance.expire') : t('echeance.jours', { jours })}
        </span>

        <span style={{ width: 120, flexShrink: 0, textAlign: 'center' }}>
          <BadgeRelances delais={delai} />
        </span>

        <span style={{ width: 104, flexShrink: 0, display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <Tooltip title={enCours ? t('relances.envoiEnCours') : t('dossier.relancer')}>
            <Button
              size="small"
              icon={enCours ? undefined : <MailOutlined />}
              loading={enCours}
              disabled={enCours}
              type="text"
              style={{ color: '#f97316' }}
              onClick={() => onRelancer(delai.id)}
            />
          </Tooltip>
          <Button
            size="small"
            type="primary"
            icon={<CheckCircleOutlined />}
            style={{
              background: '#1a6b3c', borderColor: '#1a6b3c', color: '#fff',
              borderRadius: 6, fontSize: 11,
            }}
            onClick={() => onResoudre(delai.id)}
          >
            {t('dossier.resolu')}
          </Button>
        </span>
      </div>
    </div>
  );
}

function DelaisSinistreTable({ delais, onResoudre, onRelancer, relanceEnCours }) {
  const liste = delais || [];
  if (liste.length === 0) return null;
  return (
    <div style={{ padding: '2px 0 10px 0', background: '#fff' }}>
      <EnteteDelais />
      {liste.map((d, i) => (
        <BrancheDelai
          key={d.id}
          delai={d}
          derniere={i === liste.length - 1}
          onResoudre={onResoudre}
          onRelancer={onRelancer}
          relanceEnCours={relanceEnCours}
        />
      ))}
    </div>
  );
}

function AlertesTab() {
  const { t } = useTranslation("delais");
  const [alertes, setAlertes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState("");
  const [resolveModal, setResolveModal] = useState({ open: false, id: null });
  const [motif, setMotif] = useState("");
  /* Un délai relancé sort de cette liste le temps du repos (paramètre système
     RELANCE_REPOS_JOURS) : la bascule permet de le revoir sans attendre. */
  const [inclureRelancees, setInclureRelancees] = useState(false);

  const load = async (avecRelancees = inclureRelancees) => {
    setLoading(true);
    try {
      const res = await axiosInstance
        .get("/v1/delais/urgents", { params: { inclureRelancees: avecRelancees } })
        .catch(() => ({ data: { data: [] } }));
      setAlertes(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(inclureRelancees);
  }, [inclureRelancees]); // eslint-disable-line react-hooks/exhaustive-deps

  const resoudre = async () => {
    try {
      await axiosInstance.patch(
        `/v1/delais/${resolveModal.id}/resoudre?motif=${encodeURIComponent(motif)}`,
      );
      toast.success(t("alertes.messages.resolu"));
      setResolveModal({ open: false, id: null });
      setMotif("");
      load();
    } catch {
      toast.error(t("alertes.messages.erreur"));
    }
  };

  /* Envois de relance en cours, par clé de bouton (id de délai, ou clé de dossier
     pour le bouton qui relance tout un dossier). Sans cet état, le clic ne
     produisait aucun signal : le mail partait, l'agent ne voyait rien et
     recliquait - autant de relances réellement envoyées au destinataire. */
  const [relancesEnCours, setRelancesEnCours] = useState([]);
  /* LES LIGNES OUVERTES SONT TENUES PAR L'ECRAN, comme dans « Sinistres liés ».

     Resoudre un delai ou envoyer une relance recharge la liste : `grouped` est
     recalcule, et une expansion non controlee se refermait a chaque action. Sur un
     dossier qui porte quatre delais, cela faisait quatre deplier-refermer pour les
     traiter - et l'on perdait de vue lesquels restaient. */
  const [clesOuvertes, setClesOuvertes] = useState([]);
  const enCours = (cle) => relancesEnCours.includes(cle);
  const marquerEnCours = (cles, actif) =>
    setRelancesEnCours((prev) =>
      actif ? [...prev, ...cles] : prev.filter((c) => !cles.includes(c)),
    );

  /* `skipErrorToast` : l'intercepteur axios affiche déjà un toast d'erreur
     générique. On le neutralise pour que le toast « en cours » devienne
     lui-même le toast d'échec, au lieu d'en empiler deux. */
  const envoyerRelance = (id) =>
    axiosInstance.post(`/v1/delais/${id}/relancer`, null, { skipErrorToast: true });

  /* UNE RELANCE PART PAR COURRIEL, ET A PLUS DE MONDE QU'ON NE CROIT.

     Le bouton partait au clic. Or les destinataires dependent du NIVEAU de l'alerte :
     le redacteur responsable toujours, les profils CSS si l'alerte est urgente, les
     profils SE en plus si elle est critique. Cliquer « Relancer » sur un delai critique
     ecrit donc a trois cercles de personnes, et rien ne le disait.

     Un courriel parti ne se rappelle pas. La confirmation nomme qui va le recevoir. */
  const confirmerRelance = (lignes, titre, onOk) => {
    Modal.confirm({
      title: titre,
      icon: <MailOutlined style={{ color: '#f97316' }} />,
      width: 480,
      content: (
        <div style={{ marginTop: 10 }}>
          <div style={{ color: '#374151', marginBottom: 8 }}>{t('alertes.confirmation.destinataires')}</div>
          <ul style={{ margin: 0, paddingLeft: 20, color: '#111827' }}>
            {lignes.map((l) => <li key={l} style={{ marginBottom: 4 }}>{l}</li>)}
          </ul>
          <div style={{ marginTop: 12, color: '#6b7280', fontSize: 12 }}>
            {t('alertes.confirmation.irrevocable')}
          </div>
        </div>
      ),
      okText: t('alertes.confirmation.envoyer'),
      cancelText: t('alertes.confirmation.annuler'),
      okButtonProps: { style: { background: '#f97316', borderColor: '#f97316', borderRadius: 8 } },
      cancelButtonProps: { style: { borderRadius: 8 } },
      onOk,
    });
  };

  const envoyerUneRelance = async (id) => {
    if (enCours(id)) return;
    marquerEnCours([id], true);
    const toastId = toast.loading(t("alertes.messages.envoiRelance"));
    try {
      await envoyerRelance(id);
      toast.update(toastId, {
        render: t("alertes.messages.relanceEnvoyee"),
        type: "success",
        isLoading: false,
        autoClose: 3500,
      });
      await load();
    } catch {
      toast.update(toastId, {
        render: t("alertes.messages.relanceEchouee"),
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      marquerEnCours([id], false);
    }
  };

  /** Point d'entree des boutons : on confirme, puis on envoie. */
  const relancer = (id) => {
    const delai = alertes.find((d) => d.id === id);
    confirmerRelance(
      destinatairesRelance(delai),
      t('alertes.confirmation.titre'),
      () => envoyerUneRelance(id),
    );
  };

  /* Relance de tous les délais d'un dossier : un seul toast et un seul
     rechargement pour N envois, et on attend réellement la fin. L'ancien
     `forEach(relancer)` partait en parallèle sans être attendu - les échecs
     passaient inaperçus et le compte des envois n'était jamais annoncé. */
  const envoyerRelancesDossier = async (dossier) => {
    const ids = (dossier.delais || []).map((d) => d.id);
    if (ids.length === 0 || enCours(dossier.key)) return;
    marquerEnCours([dossier.key, ...ids], true);
    const toastId = toast.loading(
      t("alertes.messages.envoiRelances", { count: ids.length }),
    );
    try {
      const resultats = await Promise.allSettled(ids.map(envoyerRelance));
      const ok = resultats.filter((r) => r.status === "fulfilled").length;
      const echecs = ids.length - ok;
      toast.update(toastId, {
        render:
          echecs === 0
            ? t("alertes.messages.relancesEnvoyees", { count: ok })
            : ok === 0
              ? t("alertes.messages.relancesEchouees")
              : t("alertes.messages.relancesPartielles", { count: ok, echecs }),
        type: echecs === 0 ? "success" : ok === 0 ? "error" : "warning",
        isLoading: false,
        autoClose: echecs === 0 ? 3500 : 5000,
      });
      await load();
    } finally {
      marquerEnCours([dossier.key, ...ids], false);
    }
  };

  // Groupement par sinistre, puis filtre par niveau si sélectionné
  const grouped = groupBySinistre(
    filtre ? alertes.filter((a) => a.niveauAlerte === filtre) : alertes,
  );

  // Colonnes de la table principale (1 ligne = 1 sinistre)
  /* LA RELANCE GROUPEE ENVOIE UN COURRIEL PAR DELAI, et les niveaux peuvent differer
     au sein d'un meme dossier : la confirmation fait l'union des destinataires, sans
     quoi un dossier portant un delai critique et un delai modere n'annoncerait que les
     destinataires du premier. */
  const relancerDossier = (dossier) => {
    const delais = dossier.delais || [];
    if (delais.length === 0 || enCours(dossier.key)) return;
    confirmerRelance(
      destinatairesDossier(delais),
      t('alertes.confirmation.titreDossier', { relances: compteRelances(delais.length) }),
      () => envoyerRelancesDossier(dossier),
    );
  };

  const colsSinistre = [
    {
      title: t("alertes.colonnes.pireAlerte"),
      dataIndex: "pireNiveau",
      key: "niv",
      width: 110,
      render: (v) => (
        <Tag color={tagColor(v)} style={{ borderRadius: 20, fontWeight: 600 }}>
          {niveauConfig[v]?.label || v}
        </Tag>
      ),
    },
    {
      title: t("alertes.colonnes.numeroSinistre"),
      dataIndex: "sinistreNumeroLocal",
      key: "sin",
      width: 180,
      render: (v) => (
        <span
          style={{
            fontWeight: 700,
            color: "#1a6b3c",
            fontSize: 13,
            letterSpacing: "0.02em",
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: t("alertes.colonnes.delaisActifs"),
      dataIndex: "delais",
      key: "nb",
      width: 110,
      align: "center",
      render: (d, r) => {
        const cfg = niveauConfig[r.pireNiveau];
        return (
          <span
            style={{
              background: cfg.bg,
              color: cfg.color,
              border: `1px solid ${cfg.color}`,
              borderRadius: 20,
              padding: "2px 10px",
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            {t("alertes.nbDelais", { count: d.length })}
          </span>
        );
      },
    },
    {
      title: t("alertes.colonnes.prochaineEcheance"),
      key: "ech",
      width: 160,
      render: (_, r) =>
        r.prochainEcheance ? (
          <span
            style={{
              fontWeight: 600,
              color:
                r.prochainEcheance.joursRestants <= 0 ? "#ef4444" : "#374151",
            }}
          >
            {formatDate(r.prochainEcheance.dateEcheance)}
            <span
              style={{
                marginLeft: 8,
                color:
                  r.prochainEcheance.joursRestants <= 0
                    ? "#ef4444"
                    : r.prochainEcheance.joursRestants <= 7
                      ? "#f97316"
                      : "#6b7280",
                fontWeight: 700,
                fontSize: 12,
              }}
            >
              (
              {r.prochainEcheance.joursRestants <= 0
                ? t("echeance.expire")
                : t("echeance.jours", { jours: r.prochainEcheance.joursRestants })}
              )
            </span>
          </span>
        ) : (
          "-"
        ),
    },
    {
      title: t("alertes.colonnes.relances"),
      key: "rel",
      width: 130,
      align: "center",
      render: (_, r) => <BadgeRelances delais={r.delais} />,
    },
    {
      title: "",
      key: "act",
      width: 60,
      align: "center",
      render: (_, r) => {
        const actif = enCours(r.key);
        return (
          <Tooltip
            title={
              actif
                ? t("relances.envoiEnCours")
                : t("alertes.relancerDossier")
            }
          >
            <Button
              size="small"
              icon={actif ? undefined : <MailOutlined />}
              loading={actif}
              disabled={actif}
              type="text"
              style={{ color: "#f97316" }}
              onClick={(e) => {
                e.stopPropagation();
                relancerDossier(r);
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      {/* KPIs */}
      <KpiCards items={Object.entries(niveauConfig).map(([key, cfg]) => ({
        label: cfg.label,
        value: alertes.filter((a) => a.niveauAlerte === key).length,
        color: cfg.color,
        bg: cfg.bg,
        active: filtre === key,
        onClick: () => setFiltre(filtre === key ? "" : key),
      }))} />

      <div
        className="cb-card"
        style={{
          marginBottom: 12,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Select
          value={filtre || undefined}
          onChange={(v) => setFiltre(v || "")}
          placeholder={t("alertes.filtres.tousNiveaux")}
          style={{ width: 200 }}
          allowClear
        >
          {Object.entries(niveauConfig).map(([k, v]) => (
            <Option key={k} value={k}>
              <Tag color={tagColor(k)}>{v.label}</Tag>
            </Option>
          ))}
        </Select>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => load()}
          style={{ borderRadius: 8 }}
        >
          {t("alertes.filtres.actualiser")}
        </Button>
        <Tooltip title={t("alertes.filtres.inclureRelanceesAide")}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6b7280" }}>
            <Switch size="small" checked={inclureRelancees} onChange={setInclureRelancees} />
            {t("alertes.filtres.inclureRelancees")}
          </span>
        </Tooltip>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
          {t("alertes.nbDossiers", { count: grouped.length })} ·{" "}
          {t("alertes.nbAlertes", { count: alertes.length })}
        </span>
      </div>

      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          dataSource={grouped}
          columns={colsSinistre}
          rowKey="key"
          loading={loading}
          scroll={{ x: 700 }}
          rowClassName={(r) =>
            r.pireNiveau === "CRITIQUE"
              ? "delai-critique"
              : r.pireNiveau === "URGENT"
                ? "delai-urgent"
                : ""
          }
          pagination={{
            pageSize: 20,
            showTotal: (total) => t("alertes.nbDossiers", { count: total }),
          }}
          locale={{
            emptyText: (
              <Empty
                description={inclureRelancees ? t("alertes.aucuneUrgente") : t("alertes.aucuneARelancer")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          expandable={{
            expandedRowKeys: clesOuvertes,
            onExpandedRowsChange: setClesOuvertes,
            expandedRowRender: (record) => (
              <DelaisSinistreTable
                delais={record.delais}
                onResoudre={(id) => setResolveModal({ open: true, id })}
                onRelancer={relancer}
                relanceEnCours={enCours}
              />
            ),
            rowExpandable: (r) => r.delais.length > 0,
            expandIcon: ({ expanded, onExpand, record }) => (
              <RightOutlined
                rotate={expanded ? 90 : 0}
                onClick={(e) => onExpand(record, e)}
                style={{
                  color: "#1a6b3c",
                  transition: "transform 0.2s",
                  cursor: "pointer",
                }}
              />
            ),
          }}
        />
      </div>

      <CBModal
        title={t("alertes.resolution.titre")}
        open={resolveModal.open}
        onCancel={() => setResolveModal({ open: false, id: null })}
        onOk={resoudre}
        okText={t("alertes.resolution.confirmer")}
        okButtonProps={{
          style: { background: "#1a6b3c", borderColor: "#1a6b3c" },
        }}
      >
        <p style={{ marginBottom: 12, color: "#6b7280" }}>
          {t("alertes.resolution.expliquer")}
        </p>
        <Input.TextArea
          rows={3}
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          placeholder={t("alertes.resolution.exemple")}
        />
      </CBModal>
    </>
  );
}

//  Onglet Référentiel délais 
function ReferentielTab() {
  const { t } = useTranslation("delais");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, row: null });
  const [form] = Form.useForm();
  const [filtreCategorie, setFiltreCategorie] = useState("");
  const [recherche, setRecherche] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance
        .get("/v1/delais/parametres")
        .catch(() => ({ data: { data: [] } }));
      setRows(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openEdit = (row) => {
    form.setFieldsValue({
      valeur: row.valeur,
      seuilAlerte1Pct: row.seuilAlerte1Pct,
      seuilAlerte2Pct: row.seuilAlerte2Pct,
      tauxPenalitePct: row.tauxPenalitePct,
    });
    setEditModal({ open: true, row });
  };

  const saveEdit = async () => {
    const vals = form.getFieldsValue();
    try {
      await axiosInstance.put(
        `/v1/delais/parametres/${editModal.row.id}`,
        vals,
      );
      toast.success(t("parametres.misAJour"));
      setEditModal({ open: false, row: null });
      load();
    } catch {
      toast.error(t("parametres.erreurMiseAJour"));
    }
  };

  const rowsFiltrees = rows.filter((r) => {
    if (filtreCategorie && r.categorie !== filtreCategorie) return false;
    if (recherche) {
      const q = recherche.trim().toLowerCase();
      const hay = `${r.codeDelai || ""} ${r.libelle || ""} ${r.libelleEn || ""} ${r.referenceJuridique || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const cols = [
    {
      title: t("referentiel.colonnes.code"),
      dataIndex: "codeDelai",
      key: "code",
      width: 130,
      render: (v) => (
        <code
          style={{
            fontSize: 12,
            background: "#f3f4f6",
            padding: "2px 6px",
            borderRadius: 4,
          }}
        >
          {v}
        </code>
      ),
    },
    { title: t("referentiel.colonnes.libelle"), dataIndex: "libelle", key: "lib", ellipsis: true, render: (v, r) => libelleTraduit(v, r.libelleEn) },
    {
      title: t("referentiel.colonnes.categorie"),
      dataIndex: "categorie",
      key: "cat",
      width: 120,
      render: (v) => (
        <Tag style={{ borderRadius: 12 }}>{CATEGORIE_LABELS[v] || v}</Tag>
      ),
    },
    {
      title: t("referentiel.colonnes.type"),
      dataIndex: "typeDelai",
      key: "typ",
      width: 100,
      render: (v) => {
        const col =
          v === "PENALITE_AUTO"
            ? "red"
            : v === "PRESCRIPTION"
              ? "purple"
              : v === "INTERNE"
                ? "blue"
                : "default";
        return (
          <Tag color={col} style={{ borderRadius: 12 }}>
            {TYPE_LABELS[v] || v}
          </Tag>
        );
      },
    },
    {
      title: t("referentiel.colonnes.duree"),
      key: "dur",
      width: 110,
      align: "center",
      render: (_, r) => (
        <span style={{ fontWeight: 700 }}>
          {r.valeur} {UNITE_LABELS[r.unite] || r.unite}
        </span>
      ),
    },
    {
      title: t("referentiel.colonnes.seuils"),
      key: "seuils",
      width: 140,
      render: (_, r) => (
        <span style={{ fontSize: 12 }}>
          {r.seuilAlerte1Pct}% / {r.seuilAlerte2Pct}%
        </span>
      ),
    },
    {
      title: t("referentiel.colonnes.penalite"),
      dataIndex: "tauxPenalitePct",
      key: "pen",
      width: 90,
      align: "center",
      render: (v) => (v ? <Tag color="red">{t("referentiel.penaliteParMois", { taux: v })}</Tag> : "-"),
    },
    {
      title: t("referentiel.colonnes.referenceLegale"),
      dataIndex: "referenceJuridique",
      key: "ref",
      ellipsis: true,
      render: (v) => (
        <span style={{ fontSize: 11, color: "#6b7280" }}>{v}</span>
      ),
    },
    {
      title: "",
      key: "act",
      width: 60,
      align: "center",
      // Deux raisons distinctes de ne pas pouvoir modifier, et il faut les dire
      // separement : le delai est REGLEMENTAIRE (personne ne le change), ou
      // l'utilisateur n'est PAS HABILITE (un autre le changerait). Afficher le
      // bouton sans l'habilitation menait droit a un 403 sans explication.
      render: (_, r) =>
        !r.modifiable ? (
          <Tooltip title={t("referentiel.nonModifiable")}>
            <LockOutlined style={{ color: "#9ca3af" }} />
          </Tooltip>
        ) : can(HAB.DELAI_PARAMETRER) ? (
          <Tooltip title={t("referentiel.modifier")}>
            <Button
              size="small"
              icon={<EditOutlined />}
              type="text"
              style={{ color: "#1a6b3c" }}
              onClick={() => openEdit(r)}
            />
          </Tooltip>
        ) : (
          <Tooltip title={t("referentiel.nonHabilite")}>
            <LockOutlined style={{ color: "#d1d5db" }} />
          </Tooltip>
        ),
    },
  ];

  return (
    <>
      <div
        className="cb-card"
        style={{
          marginBottom: 12,
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <Input.Search
          placeholder={t("referentiel.filtres.recherche")}
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          allowClear
          style={{ width: 280 }}
        />
        <Select
          value={filtreCategorie || undefined}
          onChange={(v) => setFiltreCategorie(v || "")}
          placeholder={t("referentiel.filtres.toutesCategories")}
          style={{ width: 200 }}
          allowClear
        >
          {Object.entries(CATEGORIE_LABELS).map(([k, v]) => (
            <Option key={k} value={k}>{v}</Option>
          ))}
        </Select>
        {(filtreCategorie || recherche) && (
          <Button size="small" onClick={() => { setFiltreCategorie(""); setRecherche(""); }}>
            {t("referentiel.filtres.reinitialiser")}
          </Button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
          {t("referentiel.nbDelais", { count: rowsFiltrees.length })}
        </span>
      </div>

      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          dataSource={rowsFiltrees}
          columns={cols}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{ pageSize: 40, showTotal: (total) => t("referentiel.totalDelais", { total }) }}
          locale={{
            emptyText: (
              <Empty
                description={t("parametres.aucun")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>


      <CBModal
        title={t("parametres.modaleTitre", { nom: editModal.row?.codeDelai })}
        open={editModal.open}
        onCancel={() => setEditModal({ open: false, row: null })}
        onOk={saveEdit}
        okText={t("parametres.enregistrer")}
        okButtonProps={{
          style: { background: "#1a6b3c", borderColor: "#1a6b3c" },
        }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            label={t("referentiel.modale.duree", { unite: UNITE_LABELS[editModal.row?.unite] || "" })}
            name="valeur"
          >
            <CBInputNumber min={0} step={1} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label={t("referentiel.modale.seuil1")}
            name="seuilAlerte1Pct"
          >
            <CBInputNumber
              min={1}
              max={99}
              step={5}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item label={t("referentiel.modale.seuil2")} name="seuilAlerte2Pct">
            <CBInputNumber
              min={1}
              max={99}
              step={5}
              style={{ width: "100%" }}
            />
          </Form.Item>
          {editModal.row?.typeDelai === "PENALITE_AUTO" && (
            <Form.Item label={t("referentiel.modale.tauxPenalite")} name="tauxPenalitePct">
              <CBInputNumber
                min={0}
                max={100}
                step={0.5}
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}
        </Form>
      </CBModal>
    </>
  );
}

//  Onglet Paramètres système 
function ParamSystemeTab() {
  const { t } = useTranslation("delais");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState({ open: false, row: null });
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance
        .get("/v1/delais/parametres-systeme")
        .catch(() => ({ data: { data: [] } }));
      setRows(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openEdit = (row) => {
    form.setFieldsValue({ valeurDecimal: row.valeurDecimal });
    setEditModal({ open: true, row });
  };

  const saveEdit = async () => {
    const vals = form.getFieldsValue();
    try {
      await axiosInstance.put(
        `/v1/delais/parametres-systeme/${editModal.row.id}`,
        vals,
      );
      toast.success(t("parametres.misAJour"));
      setEditModal({ open: false, row: null });
      load();
    } catch {
      toast.error(t("parametres.erreurMiseAJour"));
    }
  };

  const cols = [
    {
      title: t("systeme.colonnes.cle"),
      dataIndex: "cle",
      key: "cle",
      width: 200,
      render: (v) => (
        <code
          style={{
            fontSize: 12,
            background: "#f3f4f6",
            padding: "2px 6px",
            borderRadius: 4,
          }}
        >
          {v}
        </code>
      ),
    },
    { title: t("systeme.colonnes.libelle"), dataIndex: "libelle", key: "lib" },
    {
      title: t("systeme.colonnes.valeur"),
      dataIndex: "valeurDecimal",
      key: "val",
      width: 140,
      align: "center",
      render: (v) => (
        <span style={{ fontWeight: 700, fontSize: 16, color: "#1a6b3c" }}>
          {v} %
        </span>
      ),
    },
    {
      title: t("systeme.colonnes.description"),
      dataIndex: "description",
      key: "desc",
      ellipsis: true,
      render: (v) => (
        <span style={{ fontSize: 12, color: "#6b7280" }}>{v}</span>
      ),
    },
    {
      title: "",
      key: "act",
      width: 60,
      align: "center",
      render: (_, r) => (
        <Button
          size="small"
          icon={<EditOutlined />}
          type="text"
          style={{ color: "#1a6b3c" }}
          onClick={() => openEdit(r)}
        />
      ),
    },
  ];

  return (
    <>
      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          dataSource={rows}
          columns={cols}
          rowKey="id"
          loading={loading}
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                description={t("parametres.aucun")}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </div>

      <CBModal
        title={t("parametres.modaleTitre", { nom: editModal.row?.libelle })}
        open={editModal.open}
        onCancel={() => setEditModal({ open: false, row: null })}
        onOk={saveEdit}
        okText={t("parametres.enregistrer")}
        okButtonProps={{
          style: { background: "#1a6b3c", borderColor: "#1a6b3c" },
        }}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item label={t("systeme.modale.valeur")} name="valeurDecimal">
            <CBInputNumber
              min={0}
              max={100}
              step={0.5}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </CBModal>
    </>
  );
}

//  Page principale 
export default function Delais() {
  const { t } = useTranslation("delais");
  const items = [
    { key: "alertes", label: t("page.ongletAlertes"), children: <AlertesTab /> },
    {
      key: "referentiel",
      label: t("page.ongletReferentiel"),
      children: <ReferentielTab />,
    },
    {
      key: "systeme",
      label: t("page.ongletSysteme"),
      children: <ParamSystemeTab />,
    },
  ];

  return (
    <Layout title={t("page.titreOnglet")}>
      {/* Pas de `compte` : cet ecran n'est qu'un porte-onglets, chaque onglet
          charge sa propre liste dans un composant separe. */}
      <PageHeader titre={t("page.titre")} icone={<ClockCircleOutlined />} />
      <div className="cb-card" style={{ padding: "0 24px" }}>
        <Tabs defaultActiveKey="alertes" items={items} />
      </div>
    </Layout>
  );
}
